import {MatSnackBar} from '@angular/material/snack-bar';

/**
 * What a snackbar is telling the user.
 *
 * Neutral is a plain confirmation -- "Folder removed" -- and carries no icon.
 * The others are drawn with a status icon by the `mm-snack--*` classes in
 * _material-overrides.scss. Errors were the reason for this: a third of the
 * app's snackbars are errors, and they used to look exactly like the
 * confirmations around them.
 */
export type SnackKind = 'neutral' | 'success' | 'error' | 'warning';

/**
 * How long an error stays up when the caller does not say.
 *
 * Longer than a confirmation's 2-4 seconds: an error is the message someone
 * most needs to finish reading, and usually the one that sends them back to
 * try again.
 */
export const ERROR_SNACK_DURATION = 7000;

/**
 * Open a snackbar of the given kind.
 *
 * The single implementation behind every component's showSnackBar -- there
 * were five copies of the same three lines, which is how a restyle ends up
 * applying in one place and not the others.
 */
export function openSnack(snackBar: MatSnackBar, message: string, action: string,
                          duration: number, kind: SnackKind = 'neutral') {
  return snackBar.open(message, action, {
    duration,
    panelClass: kind === 'neutral' ? undefined : `mm-snack--${kind}`,
    // An error is announced straight away rather than queued behind whatever
    // the screen reader is saying -- it usually explains why something the
    // user just did did not happen.
    politeness: kind === 'error' ? 'assertive' : 'polite',
  });
}
